import type { DateRange } from '@teachino/shared-design-system';
import { graphql, type ResultOf } from '@teachino/shared-infrastructure';
import { DateTime } from 'luxon';

export const courseTopicFragment = graphql(`
  fragment CourseTopic on TopicInstance {
    id
    __typename
    startDate
    endDate
    topic {
      id
      name
      goals
      topicStatistics {
        linkedItemsTotal
      }
      usage
      permissions {
        canDeleteTopicFromOwnLibrary
        canDuplicateTopic
      }
    }
    statistics {
      plannedItemInstances
      doneItemInstances
      visibleItemInstances
    }
  }
`);

type CourseTopicFragment = ResultOf<typeof courseTopicFragment>;

export type CourseTopic = {
  id: string;
  topicInstanceId: string;
  name: string;
  goals: string | null;
  range: DateRange | null;
  numberOfMaterials: number;
  plannedItemInstances: number;
  doneItemInstances: number;
  visibleItemInstances: number;
  usage: CourseTopicFragment['topic']['usage'];
  permissions: NonNullable<CourseTopicFragment['topic']['permissions']>;
};

export function convertCourseTopicFragment(
  courseTopic: CourseTopicFragment,
): CourseTopic {
  const { id: topicInstanceId, startDate, endDate } = courseTopic;
  const { id, name, goals } = courseTopic.topic;
  const numberOfMaterials =
    courseTopic.topic.topicStatistics?.linkedItemsTotal ?? 0;

  const plannedItemInstances =
    courseTopic.statistics?.plannedItemInstances ?? 0;
  const doneItemInstances = courseTopic.statistics?.doneItemInstances ?? 0;
  const visibleItemInstances: number =
    courseTopic.statistics?.visibleItemInstances ?? 0;
  const range =
    startDate && endDate
      ? {
          start: DateTime.fromISO(startDate),
          end: DateTime.fromISO(endDate),
        }
      : null;
  const usage = courseTopic.topic.usage;
  const permissions = courseTopic.topic.permissions;

  if (!permissions) {
    throw new Error(
      `Received CourseTopic without Permissions. ${JSON.stringify(courseTopic)}`,
    );
  }
  return {
    id,
    topicInstanceId,
    name,
    goals,
    range,
    numberOfMaterials,
    plannedItemInstances,
    doneItemInstances,
    visibleItemInstances,
    usage,
    permissions,
  };
}
